import { useEffect, useState } from 'react';
import { Image, Skeleton } from '@/shared/ui';
import { VistoryService } from '../services/vistory-service';
import type { Vistory, VistoryPhoto } from '../types/vistory.types';

interface Props {
  vistory: Vistory;
  agencyId: string;
  photos?: Array<{ key: string; content_type: string }>;
}

/**
 * Fetches a signed view URL for a single vistory photo, returning null on failure.
 */
async function fetchPhotoUrl(agencyId: string, vistoryId: string, key: string): Promise<string | null> {
  try {
    // `url`, not `view_url`: the presign returns {url} and reading the
    // other name gave every img a src of undefined.
    const { url } = await VistoryService.getPhotoViewUrl(agencyId, vistoryId, key);
    return url;
  } catch { return null; }
}

/**
 * Resolves signed view URLs for a list of photo keys, keyed by key.
 */
async function resolveUrls(
  agencyId: string, vistoryId: string, keys: string[],
): Promise<Record<string, string>> {
  const result: Record<string, string> = {};
  for (const key of keys) {
    const url = await fetchPhotoUrl(agencyId, vistoryId, key);
    if (url) result[key] = url;
  }
  return result;
}

/**
 * Grid of photo thumbnails. If `photos` prop is passed (room-scoped list),
 * renders those; otherwise renders the flat `vistory.photos`.
 */
export function VistoryPhotoGallery({ vistory, agencyId, photos }: Props) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const displayPhotos: Array<{ key: string; content_type?: string; filename?: string }> =
    photos ?? vistory.photos;

  useEffect(() => {
    let cancelled = false;
    const keys = displayPhotos.map((p) => p.key);
    if (keys.length > 0) {
      resolveUrls(agencyId, vistory.id, keys).then((resolved) => {
        if (!cancelled) setUrls(resolved);
      });
    }
    return () => { cancelled = true; };
  }, [agencyId, vistory.id, displayPhotos.length]);

  if (displayPhotos.length === 0) return null;

  return (
    <div className="grid grid-cols-3 gap-[var(--space-2)] sm:grid-cols-4">
      {displayPhotos.map((photo) => (
        <PhotoThumbnail key={photo.key} photoKey={photo.key} url={urls[photo.key]} />
      ))}
    </div>
  );
}

/**
 * Renders a single photo thumbnail, showing a skeleton until its URL resolves.
 */
function PhotoThumbnail({ photoKey, url }: { photoKey: string; url: string | undefined }) {
  if (!url) {
    return <Skeleton className="aspect-square rounded-(--radius-md)" />;
  }
  return (
    <Image
      src={url}
      alt={photoKey}
      className="aspect-square w-full rounded-(--radius-md) object-cover border border-[var(--border-subtle)]"
    />
  );
}
